// ===========================================
// Contrats — Page liste refondue (UX + courtage assurance)
// KPIs + filtres inline + filtre Statut en column header + dropdown actions
// ===========================================
import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
    Table, Card, Input, Select, Button, Tag, Grid,
    message, DatePicker, Dropdown, Empty, List, Pagination, Spin, Tooltip, Modal,
} from 'antd';
import {
    SearchOutlined, PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined,
    FileProtectOutlined, DownloadOutlined, FilePdfOutlined, FileExcelOutlined,
    MoreOutlined, CheckCircleOutlined, CalendarOutlined,
    DollarOutlined, ClearOutlined, ReloadOutlined, QuestionCircleOutlined, CloseOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    contratsService, compagniesService, agencesService, typesAssuranceService,
} from '../../common/services';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import logoCap from '../../assets/logo/logo-cap-assurances.JPG';
import { formatDate, formatMontant } from '../../common/utils/format';
import { useDebounce, useReferentiel, useLatestFetch, useGlobalAgenceState, useCanSwitchAgence, useUserAgenceId, useExporterName } from '../../common/hooks';
import { addExporterFooter } from '../../common/utils/pdfExport';
import { borderExcelTable, addExcelTotals, addExcelLogoHeader, addExcelExportInfo } from '../../common/utils/excelExport';
import MobileFab from '../../components/mobileFab';
import { useHasPermission } from '../../components/can';

import MiniKpiCard from '../clients/components/MiniKpiCard';

import './contrats.page.scss';

const { Option } = Select;
const { RangePicker } = DatePicker;

// ─── Format de date en relatif ────────────────────────────────────────────
const formatRelative = (date) => {
    if (!date) return '—';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '—';
    const now = new Date();
    const diffMs = now - d;
    if (diffMs < 0) return formatDate(date);

    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);
    const diffMonth = Math.floor(diffDay / 30);
    const diffYear = Math.floor(diffDay / 365);

    if (diffMin < 1)    return "À l'instant";
    if (diffMin < 60)   return `Il y a ${diffMin} min`;
    if (diffHr  < 24)   return `Il y a ${diffHr} h`;
    if (diffDay < 30)   return diffDay === 1 ? 'Hier' : `Il y a ${diffDay} jours`;
    if (diffMonth < 12) return `Il y a ${diffMonth} mois`;
    return `Il y a ${diffYear} an${diffYear > 1 ? 's' : ''}`;
};

const STATUT_COLORS = {
    actif: 'green',
    expire: 'red',
    resilie: 'volcano',
    renouvele: 'blue',
    suspendu: 'orange',
};

const getSouscripteur = (record) => {
    const c = record.client;
    if (!c) return '—';
    return c.raisonSociale || `${c.nom || ''} ${c.prenom || ''}`.trim() || '—';
};

const Contrats = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;

    // ─── Permissions (masquer les actions non autorisées) ─────────────
    const canUpdate = useHasPermission('contrats:update');
    const canRenew = useHasPermission('contrats:renouveler');
    const canDelete = useHasPermission('contrats:delete');

    const isEcheanceFilter = searchParams.get('filter') === 'echeances';

    // ─── État ─────────────────────────────────────────────────────────
    const [contrats, setContrats] = useState([]);
    const [guideOpen, setGuideOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(true);

    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 400);
    const [filterStatut, setFilterStatut] = useState('all');
    const [filterCompagnie, setFilterCompagnie] = useState('all');
    const [filterType, setFilterType] = useState('all');
    const [filterAgence, setFilterAgence] = useGlobalAgenceState();
    const canSwitchAgence = useCanSwitchAgence();
    const userAgenceId = useUserAgenceId();
    const exporterName = useExporterName();
    const [filterDates, setFilterDates] = useState(null);

    const { data: compagnies } = useReferentiel('compagnies', () => compagniesService.getAll());
    const { data: agences } = useReferentiel('agences', () => agencesService.getAll());
    const { data: typesAssurance } = useReferentiel('typesAssurance', () => typesAssuranceService.getAll());
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const guardedFetch = useLatestFetch();

    // ─── Charger les KPIs ─────────────────────────────────────────────
    const fetchStats = async () => {
        setLoadingStats(true);
        try {
            const res = await contratsService.getStats({
                agence: filterAgence,
                typeAssurance: filterType !== 'all' ? filterType : undefined,
            });
            setStats(res?.data || null);
        } catch (err) {
            console.error('Erreur stats contrats:', err);
        } finally {
            setLoadingStats(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchStats(); }, [filterAgence, filterType]);

    // ─── Fetch contrats ───────────────────────────────────────────────
    const fetchContrats = async (page = 1, pageSize = 20) => {
        setLoading(true);
        const params = {
            page, limit: pageSize, q: debouncedSearch, statut: filterStatut,
            compagnie: filterCompagnie, agence: filterAgence, typeAssurance: filterType,
            echeances: isEcheanceFilter ? 'true' : undefined,
            start: filterDates?.[0]?.format('YYYY-MM-DD'),
            end: filterDates?.[1]?.format('YYYY-MM-DD'),
        };
        try {
            await guardedFetch(
                () => contratsService.getAll(params),
                (res) => {
                    const payload = res?.data || res || {};
                    const items = payload.items || payload || [];
                    setContrats(Array.isArray(items) ? items : []);
                    setPagination((prev) => ({
                        ...prev,
                        current: page,
                        pageSize,
                        total: payload.total || items.length || 0,
                    }));
                },
                (error) => {
                    console.error('Erreur chargement contrats:', error);
                    toast.error('Erreur lors du chargement des contrats');
                },
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchContrats(1, pagination.pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, filterStatut, filterCompagnie, filterType, filterAgence, filterDates, isEcheanceFilter]);

    // ─── Compteur de filtres actifs ───────────────────────────────────
    const activeFiltersCount = useMemo(() => {
        let count = 0;
        if (filterCompagnie !== 'all') count++;
        if (filterType !== 'all') count++;
        if (filterAgence !== 'all') count++;
        if (filterDates) count++;
        return count;
    }, [filterCompagnie, filterType, filterAgence, filterDates]);

    const clearAllFilters = () => {
        setSearch('');
        setFilterStatut('all');
        setFilterCompagnie('all');
        setFilterType('all');
        setFilterAgence('all');
        setFilterDates(null);
    };

    // ─── Suppression ──────────────────────────────────────────────────
    const handleDelete = async (id) => {
        const previous = contrats;
        setContrats((prev) => prev.filter((c) => c._id !== id));
        setPagination((prev) => ({ ...prev, total: Math.max(0, (prev.total || 0) - 1) }));
        try {
            await contratsService.delete(id, { force: true });
            toast.success('Contrat supprimé');
            fetchStats();
        } catch (error) {
            setContrats(previous);
            setPagination((prev) => ({ ...prev, total: (prev.total || 0) + 1 }));
            toast.error(error?.message || 'Erreur lors de la suppression');
        }
    };

    // ─── Export helpers ───────────────────────────────────────────────
    const fmtNum = (v) => (v || 0).toLocaleString('fr-FR').replace(/\s/g, ' ');

    const fetchAllForExport = async () => {
        const hide = message.loading("Préparation de l'export...", 0);
        try {
            const params = {
                page: 1, limit: 99999, q: debouncedSearch, statut: filterStatut,
                compagnie: filterCompagnie, agence: filterAgence, typeAssurance: filterType,
                echeances: isEcheanceFilter ? 'true' : undefined,
                start: filterDates?.[0]?.format('YYYY-MM-DD'),
                end: filterDates?.[1]?.format('YYYY-MM-DD'),
            };
            const res = await contratsService.getAll(params);
            const payload = res?.data || res || {};
            const all = payload.items || payload || [];
            hide();
            return Array.isArray(all) ? all : [];
        } catch (e) {
            hide();
            toast.error("Erreur lors de la préparation de l'export");
            return [];
        }
    };

    const buildExportData = (list) => list.map((c) => ({
        police: c.numeroPolice || '',
        souscripteur: getSouscripteur(c),
        compagnie: c.compagnie?.nom || '',
        type: c.typeAssurance?.nom || '',
        primeTTC: fmtNum(c.primeTTC),
        primeTTCNum: c.primeTTC || 0,
        effet: c.dateEffet ? new Date(c.dateEffet).toLocaleDateString('fr-FR') : '',
        echeance: c.dateEcheance ? new Date(c.dateEcheance).toLocaleDateString('fr-FR') : '',
        inscritLe: c.createdAt ? new Date(c.createdAt).toLocaleDateString('fr-FR') : '',
    }));

    const exportHeaders = ['N° Police', 'Souscripteur', 'Compagnie', 'Type assurance', 'Effet', 'Échéance', 'Inscrit le', 'Prime TTC'];

    // Titre d'export enrichi de la compagnie et du type d'assurance sélectionnés
    const buildTitreExport = () => {
        const compagnieNom = filterCompagnie !== 'all'
            ? (compagnies.find((c) => c._id === filterCompagnie)?.nom || '')
            : '';
        const typeNom = filterType !== 'all'
            ? ((typesAssurance || []).find((t) => t._id === filterType)?.nom || '')
            : '';
        return ['LISTE DES CONTRATS', compagnieNom, typeNom.toUpperCase()].filter(Boolean).join(' - ');
    };

    const handleExportPDF = async () => {
        const allContrats = await fetchAllForExport();
        if (!allContrats.length) return;
        const doc = new jsPDF({ orientation: 'landscape' });
        const today = new Date().toLocaleDateString('fr-FR');
        const pageWidth = doc.internal.pageSize.width;
        const centerX = pageWidth / 2;

        doc.addImage(logoCap, 'JPEG', 14, 8, 40, 16);
        doc.setFontSize(10);
        doc.setTextColor(0);
        doc.text(today, pageWidth - 14, 15, { align: 'right' });

        const ownAgenceNom = (!canSwitchAgence && userAgenceId)
            ? (agences.find((a) => a._id === userAgenceId)?.nom || '')
            : '';
        const agenceNom = filterAgence !== 'all'
            ? (agences.find((a) => a._id === filterAgence)?.nom || '')
            : (ownAgenceNom || agences.map((a) => a.nom).join(' / ') || 'TOUTES');
        doc.setFontSize(12);
        doc.text(`AGENCE : ${agenceNom}`, centerX, 15, { align: 'center' });

        const titrePdf = buildTitreExport();
        doc.setFontSize(14);
        doc.setFont(undefined, 'bolditalic');
        doc.text(titrePdf, centerX, 30, { align: 'center' });
        doc.setFont(undefined, 'normal');

        // Période de filtre (si renseignée)
        let pdfStartY = 38;
        if (filterDates && filterDates[0] && filterDates[1]) {
            const du = filterDates[0].format('DD/MM/YYYY');
            const au = filterDates[1].format('DD/MM/YYYY');
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            const periodeLabel = 'Période du : ';
            const periodeMid = '   au   ';
            const fullPeriode = `${periodeLabel}${du}${periodeMid}${au}`;
            const totalW = doc.getTextWidth(fullPeriode);
            const startX = centerX - totalW / 2;
            doc.setTextColor(0);
            doc.text(periodeLabel, startX, 38);
            doc.setTextColor(139, 0, 0);
            doc.text(du, startX + doc.getTextWidth(periodeLabel), 38);
            doc.setTextColor(0);
            doc.text(periodeMid, startX + doc.getTextWidth(periodeLabel + du), 38);
            doc.setTextColor(139, 0, 0);
            doc.text(au, startX + doc.getTextWidth(periodeLabel + du + periodeMid), 38);
            doc.setTextColor(0);
            doc.setFont(undefined, 'normal');
            pdfStartY = 44;
        }

        const data = buildExportData(allContrats);
        const totalPrimeTTC = data.reduce((s, d) => s + (d.primeTTCNum || 0), 0);
        autoTable(doc, {
            startY: pdfStartY,
            head: [exportHeaders],
            body: data.map((d) => [d.police, d.souscripteur, d.compagnie, d.type, d.effet, d.echeance, d.inscritLe, d.primeTTC]),
            styles: { fontSize: 8, cellPadding: 3, lineColor: [0, 0, 0], lineWidth: 0.3, fillColor: false },
            headStyles: { fillColor: false, textColor: [0, 0, 0], fontStyle: 'bold' },
            footStyles: { fillColor: false, textColor: [0, 0, 0], fontStyle: 'bold' },
            alternateRowStyles: { fillColor: false },
            columnStyles: { 7: { halign: 'right' } },
            showFoot: 'lastPage',
            foot: [[
                { content: 'TOTAL', colSpan: 7, styles: { halign: 'right', fontStyle: 'bold' } },
                { content: fmtNum(totalPrimeTTC), styles: { halign: 'right', fontStyle: 'bold' } },
            ]],
        });

        addExporterFooter(doc, exporterName);
        doc.save(`contrats_${today.replace(/\//g, '-')}.pdf`);
        toast.success('PDF exporté avec succès');
    };

    const handleExportExcel = async () => {
        const allContrats = await fetchAllForExport();
        if (!allContrats.length) return;
        const data = buildExportData(allContrats);
        const today = new Date().toLocaleDateString('fr-FR');
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Contrats');
        ws.columns = [
            { width: 22 }, { width: 28 }, { width: 22 }, { width: 26 },
            { width: 14 }, { width: 14 }, { width: 16 }, { width: 16 },
        ];
        await addExcelLogoHeader(wb, ws);

        const titreXls = buildTitreExport();
        const titleRow = ws.addRow([titreXls]);
        ws.mergeCells(titleRow.number, 1, titleRow.number, exportHeaders.length);
        titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: '1A2B5F' } };
        titleRow.getCell(1).alignment = { horizontal: 'center' };

        // Période de filtre (si renseignée)
        if (filterDates && filterDates[0] && filterDates[1]) {
            const du = filterDates[0].format('DD/MM/YYYY');
            const au = filterDates[1].format('DD/MM/YYYY');
            const periodeRow = ws.addRow([`Période du ${du} au ${au}`]);
            ws.mergeCells(periodeRow.number, 1, periodeRow.number, exportHeaders.length);
            periodeRow.getCell(1).font = { bold: true, italic: true, size: 11, color: { argb: '8B0000' } };
            periodeRow.getCell(1).alignment = { horizontal: 'center' };
        }

        addExcelExportInfo(ws, { colCount: exportHeaders.length, exporterName, today });
        ws.addRow([]);

        const headerRow = ws.addRow(exportHeaders);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: '000000' }, size: 11 };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
        });
        data.forEach((d) => {
            ws.addRow([d.police, d.souscripteur, d.compagnie, d.type, d.effet, d.echeance, d.inscritLe, d.primeTTC]);
        });
        addExcelTotals(ws, { headerRow: headerRow.number, numericCols: [7], labelCol: 1 });
        borderExcelTable(ws, headerRow.number, exportHeaders.length);
        const buffer = await wb.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `contrats_${today.replace(/\//g, '-')}.xlsx`);
        toast.success('Excel exporté avec succès');
    };

    const exportMenuItems = [
        { key: 'pdf', label: 'Exporter en PDF', icon: <FilePdfOutlined style={{ color: '#C8102E' }} />, onClick: handleExportPDF },
        { key: 'excel', label: 'Exporter en Excel', icon: <FileExcelOutlined style={{ color: '#389e0d' }} />, onClick: handleExportExcel },
    ];

    // ─── Menu d'actions par ligne ─────────────────────────────────────
    const getRowActionsMenu = (record) => ({
        items: [
            canUpdate && {
                key: 'edit',
                icon: <EditOutlined />,
                label: 'Modifier',
            },
            canRenew && {
                key: 'renew',
                icon: <ReloadOutlined />,
                label: 'Renouveler',
            },
            canDelete && (canUpdate || canRenew) && { type: 'divider' },
            canDelete && {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: 'Supprimer',
                danger: true,
            },
        ].filter(Boolean),
        onClick: ({ key, domEvent }) => {
            domEvent?.stopPropagation?.();
            if (key === 'edit') {
                navigate(`/dashboard/nouveau-contrat?edit=${record._id}`);
            } else if (key === 'renew') {
                navigate(`/dashboard/nouveau-contrat?renew=${record._id}`);
            } else if (key === 'delete') {
                if (window.confirm(`Supprimer le contrat "${record.numeroPolice || ''}" ?`)) {
                    handleDelete(record._id);
                }
            }
        },
    });

    // ─── Colonnes du tableau ──────────────────────────────────────────
    const columns = useMemo(() => [
        {
            title: 'N° Police',
            dataIndex: 'numeroPolice',
            key: 'numeroPolice',
            render: (t) => <span className="contrat-cell-mono">{t || '—'}</span>,
            width: 150,
        },
        {
            title: 'Souscripteur',
            key: 'client',
            render: (_, r) => (
                <span className="contrat-cell-name">{getSouscripteur(r)}</span>
            ),
            ellipsis: true,
        },
        {
            title: 'Compagnie',
            dataIndex: ['compagnie', 'nom'],
            key: 'compagnie',
            ellipsis: true,
            responsive: ['md'],
        },
        {
            title: 'Type',
            dataIndex: ['typeAssurance', 'nom'],
            key: 'typeAssurance',
            render: (t) => <Tag color="blue">{t || '—'}</Tag>,
            width: 170,
            responsive: ['md'],
        },
        {
            title: 'Statut',
            dataIndex: 'statut',
            key: 'statut',
            width: 130,
            filters: [
                { text: 'Actif', value: 'actif' },
                { text: 'Expiré', value: 'expire' },
                { text: 'Suspendu', value: 'suspendu' },
                { text: 'Résilié', value: 'resilie' },
                { text: 'Renouvelé', value: 'renouvele' },
            ],
            filterMultiple: false,
            filteredValue: filterStatut !== 'all' ? [filterStatut] : null,
            render: (s) => s ? <Tag color={STATUT_COLORS[s] || 'default'}>{s}</Tag> : '—',
        },
        {
            title: 'Prime TTC',
            dataIndex: 'primeTTC',
            key: 'primeTTC',
            sorter: (a, b) => (a.primeTTC || 0) - (b.primeTTC || 0),
            align: 'right',
            width: 130,
            render: (v) => <span className="contrat-cell-amount">{formatMontant(v || 0)}</span>,
        },
        {
            title: 'Effet',
            dataIndex: 'dateEffet',
            key: 'dateEffet',
            width: 110,
            render: (d) => d ? formatDate(d) : '—',
            responsive: ['lg'],
        },
        {
            title: 'Échéance',
            dataIndex: 'dateEcheance',
            key: 'dateEcheance',
            width: 110,
            render: (d) => d ? formatDate(d) : '—',
            responsive: ['lg'],
        },
        {
            title: 'Inscrit',
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
            width: 130,
            render: (val) => (
                <Tooltip title={val ? formatDate(val) : '—'}>
                    <span className="contrat-cell-date">{formatRelative(val)}</span>
                </Tooltip>
            ),
            responsive: ['md'],
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 160,
            align: 'center',
            fixed: 'right',
            render: (_, record) => (
                <div className="contrat-cell-actions">
                    <Button
                        type="primary"
                        size="small"
                        icon={<EyeOutlined />}
                        className="btn-detail contrat-action-detail"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/dashboard/contrat/${record._id}`);
                        }}
                    >
                        Détail
                    </Button>
                    {(canUpdate || canRenew || canDelete) && (
                        <Dropdown
                            menu={getRowActionsMenu(record)}
                            trigger={['click']}
                            placement="bottomRight"
                        >
                            <Button
                                type="text"
                                size="small"
                                icon={<MoreOutlined />}
                                onClick={(e) => e.stopPropagation()}
                                aria-label={`Plus d'actions pour ${record.numeroPolice || ''}`}
                                className="contrat-action-more"
                            />
                        </Dropdown>
                    )}
                </div>
            ),
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [navigate, filterStatut, canUpdate, canRenew, canDelete]);

    // ─── Handler du filtre Statut (depuis le column header) ───────────
    const handleTableChange = (_pag, filters) => {
        const next = filters?.statut?.[0];
        setFilterStatut(next || 'all');
    };

    return (
        <div className="contrats-page">
            {/* Header */}
            <header className="contrats-header">
                <div className="contrats-header-text">
                    <h1 className="contrats-title">
                        <FileProtectOutlined />
                        {isEcheanceFilter ? 'Contrats arrivant à échéance' : 'Contrats'}
                    </h1>
                    <p className="contrats-subtitle">
                        Polices d'assurance souscrites et leur statut
                    </p>
                </div>
                <div className="contrats-header-actions">
                    <Button
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setGuideOpen(true)}
                        style={{ borderRadius: 8, background: '#1A2B5F', borderColor: '#1A2B5F', color: '#fff', fontWeight: 600 }}
                    >
                        {!isMobile && 'Guide'}
                    </Button>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => navigate('/dashboard/nouveau-contrat')}
                        className="contrats-btn-new"
                    >
                        {!isMobile && 'Nouveau contrat'}
                    </Button>
                </div>
            </header>

            <Modal
                open={guideOpen}
                onCancel={() => setGuideOpen(false)}
                title={<span><QuestionCircleOutlined style={{ color: '#1A2B5F', marginRight: 8 }} />Guide — Gérer les contrats</span>}
                closeIcon={(
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px',
                        background: '#C8102E', border: '1px solid #C8102E', borderRadius: 8,
                        fontSize: '0.9rem', fontWeight: 600, color: '#fff',
                    }}>
                        <CloseOutlined /> Fermer
                    </span>
                )}
                footer={<Button type="primary" onClick={() => setGuideOpen(false)}>J'ai compris</Button>}
                width={620}
            >
                <span style={{ color: '#8c8c8c' }}>
                    Cette page liste toutes les polices d'assurance souscrites et leur statut.
                </span>
                {(() => {
                    const liStyle = {
                        display: 'flex', gap: 12, padding: '14px 16px', marginBottom: 10,
                        background: '#f6f8fb', border: '1px solid #E8ECF1', borderRadius: 12, lineHeight: 1.5,
                    };
                    const icStyle = { flexShrink: 0, fontSize: 18, color: '#1A2B5F', marginTop: 2 };
                    return (
                        <div style={{ marginTop: 16 }}>
                            <div style={liStyle}>
                                <FileProtectOutlined style={icStyle} />
                                <div>
                                    <strong>1 · Indicateurs</strong> — Nombre total de contrats,
                                    actifs, à échéance… recalculés selon les filtres et l'agence.
                                </div>
                            </div>
                            <div style={liStyle}>
                                <SearchOutlined style={icStyle} />
                                <div>
                                    <strong>2 · Rechercher &amp; filtrer</strong> — Recherchez par
                                    client ou n° de police, puis filtrez par <strong>statut</strong>,
                                    <strong> compagnie</strong>, <strong>type d'assurance</strong> (risque),
                                    <strong> période</strong> (date de saisie) et agence.
                                </div>
                            </div>
                            <div style={liStyle}>
                                <CalendarOutlined style={icStyle} />
                                <div>
                                    <strong>3 · Échéances</strong> — Le statut indique si une police
                                    est active, expirée ou bientôt à échéance, pour anticiper les
                                    renouvellements.
                                </div>
                            </div>
                            <div style={liStyle}>
                                <EyeOutlined style={icStyle} />
                                <div>
                                    <strong>4 · Actions par ligne</strong> — « Détail » ouvre le
                                    contrat ; le menu <strong>⋮</strong> permet de
                                    <strong> modifier</strong>, <strong>renouveler</strong> ou
                                    <strong> supprimer</strong>. L'export PDF/Excel reprend la liste
                                    filtrée.
                                </div>
                            </div>
                            <div style={{ ...liStyle, marginBottom: 0 }}>
                                <PlusOutlined style={icStyle} />
                                <div>
                                    <strong>5 · Nouveau contrat</strong> — Le bouton « Nouveau
                                    contrat » lance l'assistant en 4 étapes (le client doit déjà
                                    exister).
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </Modal>

            {/* KPIs */}
            <section className="contrats-kpis" aria-label="Indicateurs contrats">
                <MiniKpiCard
                    icon={<FileProtectOutlined />}
                    label="Total contrats"
                    value={stats?.total}
                    tone="blue"
                    loading={loadingStats}
                />
                <MiniKpiCard
                    icon={<CheckCircleOutlined />}
                    label="Actifs"
                    value={stats?.actifs?.count}
                    suffix={stats?.actifs?.pct !== undefined ? `(${stats.actifs.pct}%)` : ''}
                    tone="green"
                    loading={loadingStats}
                />
                <MiniKpiCard
                    icon={<CalendarOutlined />}
                    label="Échéance 30j"
                    value={stats?.echeance30j?.count}
                    tone="orange"
                    loading={loadingStats}
                />
                <MiniKpiCard
                    icon={<DollarOutlined />}
                    label="Prime totale"
                    value={stats?.totalPrime !== undefined ? formatMontant(stats.totalPrime) : 0}
                    tone="purple"
                    loading={loadingStats}
                />
            </section>

            {/* Carte principale */}
            <Card className="contrats-main-card">
                {/* Toolbar inline */}
                <div className="contrats-toolbar">
                    <Input
                        allowClear
                        placeholder="Rechercher (n° police, souscripteur, immatriculation...)"
                        prefix={<SearchOutlined style={{ color: 'var(--cap-text-muted)' }} />}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="contrats-search"
                        aria-label="Rechercher un contrat"
                    />

                    <Select
                        value={filterCompagnie}
                        onChange={setFilterCompagnie}
                        className="contrats-filter"
                        aria-label="Filtrer par compagnie"
                    >
                        <Option value="all">Toutes les compagnies</Option>
                        {compagnies.map((c) => (
                            <Option key={c._id} value={c._id}>{c.nom}</Option>
                        ))}
                    </Select>

                    <Select
                        value={filterType}
                        onChange={setFilterType}
                        className="contrats-filter"
                        aria-label="Filtrer par type d'assurance"
                    >
                        <Option value="all">Tous les types d'assurance</Option>
                        {(typesAssurance || []).map((t) => (
                            <Option key={t._id} value={t._id}>{t.nom}</Option>
                        ))}
                    </Select>

                    {canSwitchAgence && (
                        <Select
                            value={filterAgence}
                            onChange={setFilterAgence}
                            className="contrats-filter"
                            aria-label="Filtrer par agence"
                        >
                            <Option value="all">Toutes les agences</Option>
                            {agences.map((a) => (
                                <Option key={a._id} value={a._id}>{a.nom}</Option>
                            ))}
                        </Select>
                    )}

                    <RangePicker
                        format="DD/MM/YYYY"
                        placeholder={['Date début', 'Date fin']}
                        onChange={setFilterDates}
                        value={filterDates}
                        className="contrats-filter-dates"
                        allowClear
                    />

                    {(activeFiltersCount > 0 || search || filterStatut !== 'all') && (
                        <Button
                            icon={<ClearOutlined />}
                            onClick={clearAllFilters}
                            className="contrats-btn-clear"
                            aria-label="Effacer tous les filtres"
                        >
                            Effacer
                        </Button>
                    )}
                    <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                        <Button icon={<DownloadOutlined />} className="contrats-btn-export">
                            Exporter
                        </Button>
                    </Dropdown>
                </div>

                {/* Table desktop / Cards mobile */}
                {isMobile ? (
                    <Spin spinning={loading}>
                        {contrats.length === 0 ? (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="Aucun contrat trouvé"
                                style={{ padding: 32 }}
                            >
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => navigate('/dashboard/nouveau-contrat')}
                                    className="contrats-btn-new"
                                >
                                    Créer un contrat
                                </Button>
                            </Empty>
                        ) : (
                            <List
                                dataSource={contrats}
                                rowKey="_id"
                                split={false}
                                renderItem={(record) => {
                                    const echeance = record.dateEcheance
                                        ? new Date(record.dateEcheance).toLocaleDateString('fr-FR')
                                        : '—';
                                    return (
                                        <Card
                                            key={record._id}
                                            size="small"
                                            className={`contrats-mobile-card statut-${record.statut || 'unknown'}`}
                                            onClick={() => navigate(`/dashboard/contrat/${record._id}`)}
                                        >
                                            <div className="mobile-card-head">
                                                <span className="mobile-card-police">{record.numeroPolice || '—'}</span>
                                                {record.statut && (
                                                    <Tag color={STATUT_COLORS[record.statut] || 'default'}>
                                                        {record.statut}
                                                    </Tag>
                                                )}
                                            </div>
                                            <div className="mobile-card-name">
                                                {getSouscripteur(record)}
                                            </div>
                                            <div className="mobile-card-meta">
                                                <span>{record.compagnie?.nom || ''}</span>
                                                <Tag color="blue">{record.typeAssurance?.nom || '—'}</Tag>
                                            </div>
                                            <div className="mobile-card-footer">
                                                <span className="mobile-card-amount">
                                                    {formatMontant(record.primeTTC || 0)}
                                                </span>
                                                <span className="mobile-card-echeance">
                                                    <CalendarOutlined /> {echeance}
                                                </span>
                                                <Dropdown
                                                    menu={getRowActionsMenu(record)}
                                                    trigger={['click']}
                                                >
                                                    <Button
                                                        type="text"
                                                        icon={<MoreOutlined />}
                                                        onClick={(e) => e.stopPropagation()}
                                                        aria-label="Actions"
                                                    />
                                                </Dropdown>
                                            </div>
                                        </Card>
                                    );
                                }}
                            />
                        )}
                        <div className="contrats-pagination-wrapper">
                            <Pagination
                                current={pagination.current}
                                pageSize={pagination.pageSize}
                                total={pagination.total}
                                onChange={(p, ps) => fetchContrats(p, ps)}
                                size="small"
                                simple
                            />
                        </div>
                    </Spin>
                ) : (
                    <Table
                        dataSource={contrats}
                        columns={columns}
                        rowKey="_id"
                        loading={{ spinning: loading, size: 'large', tip: 'Chargement…' }}
                        onChange={handleTableChange}
                        pagination={{
                            ...pagination,
                            onChange: (page, pageSize) => fetchContrats(page, pageSize),
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} contrat${total > 1 ? 's' : ''}`,
                        }}
                        size="middle"
                        scroll={{ x: 'max-content' }}
                        onRow={(record) => ({
                            onClick: () => navigate(`/dashboard/contrat/${record._id}`),
                            style: { cursor: 'pointer' },
                        })}
                        locale={{
                            emptyText: loading ? (
                                <div style={{ minHeight: 220 }} />
                            ) : (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={
                                        activeFiltersCount > 0 || search
                                            ? 'Aucun contrat ne correspond aux filtres'
                                            : 'Aucun contrat trouvé'
                                    }
                                >
                                    {(activeFiltersCount > 0 || search) ? (
                                        <Button icon={<ClearOutlined />} onClick={clearAllFilters}>
                                            Effacer les filtres
                                        </Button>
                                    ) : (
                                        <Button
                                            type="primary"
                                            icon={<PlusOutlined />}
                                            onClick={() => navigate('/dashboard/nouveau-contrat')}
                                            className="contrats-btn-new"
                                        >
                                            Créer un contrat
                                        </Button>
                                    )}
                                </Empty>
                            ),
                        }}
                    />
                )}
            </Card>

            <MobileFab
                icon={<PlusOutlined />}
                onClick={() => navigate('/dashboard/nouveau-contrat')}
                title="Nouveau contrat"
            />
        </div>
    );
};

export default Contrats;
